import { closePopup, openPopup } from "@/helper/DxHelper";
import { devLog, semverGreaterThan } from "@/helper/helper";
import Popup, { PopupRef } from "devextreme-react/cjs/popup";
import { useCallback, useEffect, useRef, useState } from "react";
import CustomButton from "./CustomButton";

const CacheBuster = () => {
  const [isLatestVersion, setIsLatestVersion] = useState(false);
  const popupRef = useRef<PopupRef>(null);

  const onConfirm = async () => {
    devLog("Reloading triggered by user...");
    if ("caches" in window) {
      const cacheKeys = await caches.keys();
      await Promise.all(cacheKeys.map((key) => caches.delete(key)));
    }
    window.location.reload();
  };

  const showPopup = async () => {
    devLog("Displaying update popup to user...");
    setTimeout(() => {
      openPopup(popupRef);
    }, 100);
  };

  const checkVersion = useCallback(async () => {
    if (popupRef.current?.instance().option("visible")) {
      devLog("Popup is open, skipping fetch to meta.json");
      return;
    }

    const now = new Date();
    devLog("Initiating version check...", { now });

    try {
      const response = await fetch("/meta.json", {
        cache: "no-store",
        headers: { Accept: "application/json" },
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        throw new Error("Response is not JSON");
      }

      const meta = await response.json();
      const latestVersion = meta.version;
      const currentVersion = APP_VERSION;

      devLog("Fetched version data", { latestVersion, currentVersion });

      if (semverGreaterThan(latestVersion, currentVersion)) {
        devLog(`New version available: ${latestVersion}`);
        setIsLatestVersion(false);
        await showPopup();
        devLog("Popup displayed to user");
      } else {
        devLog("User has the latest version, no update required", { currentVersion });
        setIsLatestVersion(true);
      }
    } catch (error) {
      devLog("Error during version check", { error });
    }
  }, []);

  useEffect(() => {
    const interval = setInterval(checkVersion, 5 * 60 * 1000);
    checkVersion();

    return () => clearInterval(interval);
  }, [checkVersion]);

  if (isLatestVersion) return null;

  return (
    <Popup width={450} height={"fit"} ref={popupRef} showTitle={false} className="shadow-light">
      <div className="flex flex-col gap-2">
        <p className="text-lg font-medium">
          A new version of the application is available. Please save any unsaved changes and refresh
          the page to get the latest version.
        </p>
        <p className="text-sm text-neutral-300">
          If you dismiss, this notification will reappear in 5 minutes unless you manually reload
          the page.
        </p>
        <div className="flex gap-4 pt-4">
          <CustomButton
            onClickHandler={onConfirm}
            variant="primary"
            className="flex-1"
            text="Refresh"
          />
          <CustomButton
            text="Dismiss"
            variant="default"
            className="flex-1"
            onClickHandler={() => closePopup(popupRef)}
          />
        </div>
      </div>
    </Popup>
  );
};

export default CacheBuster;
